'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import {
  FaGlobeEurope,
  FaRocket,
  FaBox,
  FaRepeat,
  GiCargoCrane,
  HiBeaker,
  MdOutlineSupport,
  SiLetsencrypt
} from "@/components/ReactIcons";



// Improved advantages section with custom colors
export function AdvantagesSection({
  advantageTexts,
}: {
  advantageTexts?: Record<string, { title: string; description: string }>;
}) {
  const tAdvantages = useTranslations('advantages');
  const advantagesRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Set mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Features data with keys matching translation keys
  const features = [
    { key: 'unmatchedQuality', icon: <HiBeaker className="w-full h-full" /> },
    { key: 'globalShipping', icon: <FaGlobeEurope className="w-full h-full" /> },
    { key: 'securePayments', icon: <SiLetsencrypt className="w-full h-full" /> },
    { key: 'customSolutions', icon: <GiCargoCrane className="w-full h-full" /> },
    { key: 'bulkPricing', icon: <FaBox className="w-[90%] h-[90%]" /> },
    { key: 'innovationEdge', icon: <FaRocket className="w-[90%] h-[90%]" /> },
    { key: 'reliableSupply', icon: <FaRepeat className="w-[90%] h-[90%]" /> },
    { key: 'expertSupport', icon: <MdOutlineSupport className="w-[110%] h-[110%]" /> }
  ];

  return (
    <div ref={advantagesRef} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full sm:h-full overflow-x-auto sm:overflow-hidden flex items-center">
      <div className="relative w-full">
        <div className={isMobile ? "flex flex-nowrap items-stretch" : "grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"}>
          {features.map((feature, idx) => (
            <div 
              key={`feature-${idx}`}
              className="p-6 rounded-xl backdrop-blur-sm transition-all duration-300 flex flex-col flex-1 h-full min-h-0 min-w-[200px] sm:min-w-0
                bg-static-black group"
            >
              <div className="w-10 h-10 mb-5 rounded-full bg-bg-main flex items-center justify-center text-primary group-hover:text-accent transition-colors duration-300">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold mb-2 text-text-main group-hover:text-primary transition-colors duration-300">
                {advantageTexts?.[feature.key]?.title || tAdvantages(`${feature.key}.title`)}
              </h3>
              <p className="text-text-secondary">
                {advantageTexts?.[feature.key]?.description || tAdvantages(`${feature.key}.description`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Slimmed down exports, removing old unnecessary components
export function InteractiveImage(props: React.ComponentProps<typeof Image>) {
  return (
    <Image
      {...props}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      alt={props.alt}
    />
  );
}


export function OrbVideoSection({ videoSrc, className = "" }: { videoSrc: string; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Improved lazy loading implementation
    if (videoRef.current) {
      // Set the source directly for critical videos
      const videoElement = videoRef.current;
      videoElement.src = videoSrc;
      
      videoElement.addEventListener('loadeddata', () => {
        setIsLoaded(true);
        videoElement.classList.add('loaded');
      });
      
      // Fallback in case the loadeddata event doesn't fire
      setTimeout(() => {
        if (!isLoaded && videoElement) {
          videoElement.classList.add('loaded');
        }
      }, 1000);
    }
  }, [videoSrc, isLoaded]);

  return (
    <video
      ref={videoRef}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      controls
      controlsList="nodownload"
      draggable={false}
      style={{ mixBlendMode: 'screen' }}
      className={`${className} ${isLoaded ? 'loaded' : ''}`}
      autoPlay
      loop
      muted
      playsInline
    />
  );
}

export function ShippingGlobe() {
  const [showGlobe, setShowGlobe] = useState(false)

  useEffect(() => {
      const timer = setTimeout(() => {
          setShowGlobe(true)
      }, 3000) // Adjust the delay as needed (in milliseconds)
      return () => clearTimeout(timer)
  }, [])

  if (!showGlobe) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <Image
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        src="/Globe.avif"
        alt="Globe overlay"
        fill
        className="object-cover"
        loading="lazy"
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1280px"
        quality={75}  // Adjust quality for better performance
      />
    </div>
  );
}

export function EncryptionSection() {
  const tEncryption = useTranslations('encryption');
  const encryptionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pillText = tEncryption('pill');
  const headingText = tEncryption('heading');
  const descriptionText = tEncryption('description');
  const [displayText, setDisplayText] = useState('');
  const [displayHeading, setDisplayHeading] = useState('');
  const [displayDescription, setDisplayDescription] = useState('');

  useEffect(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const scramble = (str: string) =>
      str
        .split('')
        .map(c => (c === ' ' ? ' ' : chars.charAt(Math.floor(Math.random() * chars.length))))
        .join('');
    const intervalTime = 69;
    const totalDuration = 2000;
    const intervalId = setInterval(() => {
      setDisplayText(scramble(pillText));
      setDisplayHeading(scramble(headingText));
      setDisplayDescription(scramble(descriptionText));
    }, intervalTime);
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      setDisplayText(pillText);
      setDisplayHeading(headingText);
      setDisplayDescription(descriptionText);
    }, totalDuration);
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [pillText, headingText, descriptionText]);

  // Improved intersection observer implementation
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // load the video when in view
          if (videoRef.current) {
            const videoElement = videoRef.current;
            videoElement.src = videoElement.dataset.src || '';
            videoElement.classList.add('loaded');
            videoElement.play();
          }
          
          observer.disconnect();
        }
      });
    }, { threshold: 0.1, rootMargin: '100px' });

    if (encryptionRef.current) {
      observer.observe(encryptionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="relative w-full h-full flex items-center justify-center overflow-hidden"
      ref={encryptionRef}
    >
      <video
        ref={videoRef}
        onContextMenu={(e) => e.preventDefault()}
        draggable={false}
        data-src="/Encryption.webm"
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={(event) => {
          const videoElement = event.currentTarget;
          setTimeout(() => {
            videoElement.play();
          }, 3000);
        }}
        className="absolute left-1/2 -translate-x-1/2 w-[80vw] min-w-[1152px] h-full object-cover opacity-0 transition-opacity duration-500"
      />
      <div className="relative z-10 text-center px-4 space-y-4">
        <div className="relative w-fit mx-auto group">
          <div className="absolute left-1/2 -translate-x-1/2 -top-6 transition-all duration-300 group-hover:top-1">
            <Image src="/lockring.png" alt="Lock ring" width={100} height={100} />
          </div>
          <div className="relative">
            <Image src="/lock.png" alt="Lock" width={80} height={80} />
          </div>
        </div>
        <div className="my-2 inline-block rounded-full p-px bg-gradient-to-r from-purple-400/50 to-blue-400/50">
          <div
            className="inline-block px-2 py-1 rounded-full bg-bg-main/80 backdrop-blur-sm text-xs sm:text-sm font-semibold"
            style={{ boxShadow: 'inset 0 -7px 11px #a48fff1f' }}
          >
            <span className="relative bg-gradient-to-r from-purple-300 to-blue-200 bg-clip-text text-transparent">
              {displayText}
            </span>
          </div>
        </div>
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-main">
          <span>{displayHeading}</span>
        </h2>
        <p className="mt-1 sm:mt-2 text-sm sm:text-lg text-text-main/80">
          {displayDescription}
        </p>
      </div>
      {/* Optional: Global style to reveal the video when .loaded is added */}
      <style jsx global>{`
        .loaded {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
}
